import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { unwrapList } from '../lib/unwrap'

export interface DeviceGroup {
  id: string
  tenant_id: string
  name: string
  description: string
  color: string
  device_count: number
  role_count: number
  created_at: string
}

export function useDeviceGroups() {
  return useQuery({
    queryKey: ['device-groups'],
    queryFn: () => api.get('/device-groups').then((r) => unwrapList<DeviceGroup>(r.data)),
  })
}

export function useCreateDeviceGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string; color?: string }) =>
      api.post('/device-groups', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-groups'] }),
  })
}

export function useUpdateDeviceGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name: string; description?: string; color?: string }) =>
      api.put(`/device-groups/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-groups'] }),
  })
}

export function useDeleteDeviceGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/device-groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-groups'] }),
  })
}

export function useGroupDevices(groupId: string) {
  return useQuery({
    queryKey: ['device-group-devices', groupId],
    queryFn: () => api.get(`/device-groups/${groupId}/devices`).then((r) => r.data.device_ids ?? []),
    enabled: !!groupId,
  })
}

export function useAddDeviceToGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, deviceId }: { groupId: string; deviceId: string }) =>
      api.post(`/device-groups/${groupId}/devices`, { device_id: deviceId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-group-devices'] }),
  })
}

export function useRemoveDeviceFromGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, deviceId }: { groupId: string; deviceId: string }) =>
      api.delete(`/device-groups/${groupId}/devices/${deviceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-group-devices'] }),
  })
}

export function useAssignGroupToRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, roleId }: { groupId: string; roleId: string }) =>
      api.post(`/device-groups/${groupId}/assign`, { role_id: roleId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-groups'] }),
  })
}

export function useUnassignGroupFromRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, roleId }: { groupId: string; roleId: string }) =>
      api.delete(`/device-groups/${groupId}/assign/${roleId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-groups'] }),
  })
}

export function useGroupRoles(groupId: string) {
  return useQuery({
    queryKey: ['device-group-roles', groupId],
    queryFn: () => api.get(`/device-groups/${groupId}/roles`).then((r) => r.data.roles ?? []),
    enabled: !!groupId,
  })
}

export function useUserDeviceSummary(userId: string) {
  return useQuery({
    queryKey: ['user-device-summary', userId],
    queryFn: () => api.get(`/users/${userId}/device-summary`).then((r) => r.data),
    enabled: !!userId,
  })
}
